import { useEffect } from 'react'
/**
 * @file PortfolioAllocation.jsx
 * @description Main application interface with role-specific features
 * 
 * @section Responsibilities
 * - Manage role-based views
 * - Handle data aggregation
 * - Control real-time updates
 * - Manage user preferences
 * 
 * @section Dependencies
 * - Role Management: Access control
 * - Data Services: Information handling
 * - State Management: Global state
 * 
 * @section Integration Points
 * - Data API: Information retrieval
 * - Role System: Access control
 * - Notification System: Real-time updates
 * 
 * @section State Management
 * - dashboardData: Aggregated information
 * - loadingState: Data fetching
 * - errorState: Error handling
 * 
 * @section Events
 * - onDataUpdate: Data changes
 * - onError: Error handling
 * - onPreferenceChange: Settings updates
 * 
 * @section Props
 * @prop {String} userRole - Current user role
 * @prop {Object} permissions - User permissions
 */


import Widget from "../Widget";
import DgPieChart from "../../../../components/dashboard/DgPieChart";

function PortfolioAllocation({ width, data, loading }) {
  // Helper function to prepare chart data
  const prepareChartData = (userData) => {
    if (!userData || !userData.strategies) return [];
    
    // Filter active strategies and map to chart format
    return userData.strategies
      .filter(strategy => strategy.status === 'ACTIVE' || strategy.status === 1) // Adjust based on your enum
      .map(strategy => ({
        strategyName: strategy.strategyName || strategy.name || `Strategy ${strategy.strategyId}`,
        percentage: strategy.percentage || 0,
        strategyId: strategy.strategyId,
        riskScore: strategy.riskScore || 0
      }));
  };

  // Calculate total used percentage for validation
  const calculateTotalUsedPercentage = (strategies) => {
    if (!strategies) return 0;
    return strategies.reduce((total, strategy) => {
      return total + (parseFloat(strategy.percentage) || 0);
    }, 0);
  };

  const chartData = prepareChartData(data);
  const totalUsedPercentage = calculateTotalUsedPercentage(chartData);
  const availableFundsPercentage = data?.availableFundsPercentage || (100 - totalUsedPercentage);

  return (
    <Widget name={"portfolio-allocation"} width={width}>
      <div className="heading">
        <h1 className="title">Portfolio Allocation</h1>
      </div>
      <div className="score">
        <p className="number">
          {data.weightedRiskScore != null && data.weightedRiskScore !== ''
            ? data.weightedRiskScore
            : '-'}
        </p>
        <p className="risk-text">Risk Score</p>
      </div>
      <p className="description">
        Diversify your portfolio, talk to your advisor to make any changes.
      </p>
      <div className="row center">
        <DgPieChart
          data={chartData}
          value={"percentage"}
          percentageInfo={data?.timeWeightedPerformance ?? 0}
          availableFundsPercentage={availableFundsPercentage}
        />
      </div>
    </Widget>
  );
}

export default PortfolioAllocation;
